import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Package, Truck, Eye, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { shipmentService } from '../services/shipmentService';
import { useNavigate } from 'react-router-dom';

const AdminShipments = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [trackingInfo, setTrackingInfo] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.isAdmin) {
      navigate('/login');
      return;
    }
    fetchShipments();
  }, [isAuthenticated, user, navigate]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const data = await shipmentService.listAllShipments(0, 100);
      setShipments(data.shipments || []);
    } catch (error) {
      console.error('Failed to fetch shipments:', error);
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  const handleTrackShipment = async (waybill) => {
    try {
      const data = await shipmentService.trackShipment(waybill);
      setTrackingInfo(data);
      toast.success('Tracking information updated');
    } catch (error) {
      console.error('Tracking failed:', error);
      toast.error('Failed to track shipment');
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('delivered')) return 'bg-green-100 text-green-700';
    if (statusLower.includes('transit')) return 'bg-blue-100 text-blue-700';
    if (statusLower.includes('picked')) return 'bg-yellow-100 text-yellow-700';
    if (statusLower.includes('confirmed')) return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  if (!isAuthenticated || !user?.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-600">Access Denied. Admin Only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50 py-16 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-rose-900 mb-2">Shipment Management</h1>
            <p className="text-gray-600">Manage and track all deliveries</p>
          </div>
          <Button
            onClick={fetchShipments}
            className="bg-rose-700 hover:bg-rose-800 text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Shipments</p>
                <p className="text-3xl font-bold text-gray-800">{shipments.length}</p>
              </div>
              <Package className="w-12 h-12 text-rose-600 opacity-20" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Transit</p>
                <p className="text-3xl font-bold text-blue-600">
                  {shipments.filter(s => s.status?.toLowerCase().includes('transit')).length}
                </p>
              </div>
              <Truck className="w-12 h-12 text-blue-600 opacity-20" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Delivered</p>
                <p className="text-3xl font-bold text-green-600">
                  {shipments.filter(s => s.status?.toLowerCase().includes('delivered')).length}
                </p>
              </div>
              <Package className="w-12 h-12 text-green-600 opacity-20" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {shipments.filter(s => s.status?.toLowerCase().includes('pending') || s.status?.toLowerCase().includes('confirmed')).length}
                </p>
              </div>
              <Package className="w-12 h-12 text-yellow-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Shipments Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-700 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading shipments...</p>
            </div>
          ) : shipments.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No shipments found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Order ID</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Waybill</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Destination</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Payment</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {shipments.map((shipment) => (
                    <tr key={shipment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {shipment.order_id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {shipment.waybill_number || 'Pending'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {shipment.consignee_details?.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {shipment.consignee_details?.city}, {shipment.consignee_details?.postal_code}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(shipment.status)}`}>
                          {shipment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className={`px-2 py-1 rounded text-xs ${
                          shipment.payment_mode === 'COD' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {shipment.payment_mode}
                          {shipment.payment_mode === 'COD' && ` ₹${shipment.cod_amount}`}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(shipment.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        {shipment.waybill_number && (
                          <Button
                            onClick={() => handleTrackShipment(shipment.waybill_number)}
                            variant="outline"
                            size="sm"
                            className="text-rose-700 border-rose-700 hover:bg-rose-50"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Track
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Tracking Info Modal */}
        {trackingInfo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Tracking Information</h2>
                <button
                  onClick={() => setTrackingInfo(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-xs">
                {JSON.stringify(trackingInfo, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminShipments;
